import React, { useState, useMemo, useRef } from 'react';
import { Modal, Checkbox, Button, Space, Typography, Divider, message, Radio } from 'antd';
import { DownloadOutlined, CheckSquareOutlined, BorderOutlined, HolderOutlined, CloudDownloadOutlined } from '@ant-design/icons';
import { COLORS } from '../../../constants/colors';
import PatientService from '../../../services/PatientService';
import * as XLSX from 'xlsx';

const { Text } = Typography;

// State abbreviation → full name lookup
const STATE_NAMES = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',
  CO:'Colorado',CT:'Connecticut',DE:'Delaware',FL:'Florida',GA:'Georgia',
  HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',IA:'Iowa',
  KS:'Kansas',KY:'Kentucky',LA:'Louisiana',ME:'Maine',MD:'Maryland',
  MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',MS:'Mississippi',MO:'Missouri',
  MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',
  NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',
  OK:'Oklahoma',OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',
  SD:'South Dakota',TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',
  VA:'Virginia',WA:'Washington',WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',
  DC:'District of Columbia',PR:'Puerto Rico',VI:'Virgin Islands',GU:'Guam',AS:'American Samoa',
  MP:'Northern Mariana Islands'
};

/** Convert a state value to its full name. If already a full name, return as-is. */
function toFullStateName(val) {
  if (!val) return '';
  const upper = val.trim().toUpperCase();
  if (STATE_NAMES[upper]) return STATE_NAMES[upper];
  // Already a full name or unrecognised — return as-is with title case
  return val.trim();
}

/** Format a phone string to (XXX) XXX-XXXX */
function formatPhone(raw) {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  // Handle 10-digit or 11-digit (leading 1) US numbers
  const ten = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
  if (ten.length === 10) {
    return `(${ten.slice(0,3)}) ${ten.slice(3,6)}-${ten.slice(6)}`;
  }
  return raw; // return original if can't parse
}

/** Normalise gender to "Male" or "Female" */
function formatGender(val) {
  if (!val) return '';
  const lower = val.toLowerCase().trim();
  if (lower === 'male' || lower === 'm') return 'Male';
  if (lower === 'female' || lower === 'f') return 'Female';
  return val; // return as-is if non-binary / other
}

/**
 * All available export columns.
 * Default order matches the required output format:
 *   activation_code  name  email  sex  date_of_birth  phone_number
 *   residential_state  residential_zip_code  acknowledged_terms  accepted_responsibility
 */
const EXPORT_COLUMNS = [
  { key: 'activation_code',     label: 'Activation Code (Testing Code)', extract: (r) => r.fullPatientData?.testingCode || '' },
  { key: 'collection_date',     label: 'Collection Date',                extract: (r) => {
    const raw = r.checkInTimeRaw || r.completionTimeRaw;
    if (!raw) return '';
    const d = new Date(raw);
    if (isNaN(d)) return '';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}/${dd}/${d.getFullYear()}`;
  }},
  { key: 'collection_time',     label: 'Collection Time',                extract: (r) => {
    const raw = r.checkInTimeRaw || r.completionTimeRaw;
    if (!raw) return '';
    const d = new Date(raw);
    if (isNaN(d)) return '';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }},
  // Name columns — dynamic based on nameMode
  { key: 'first_name',  label: 'First Name',           extract: (r) => r.fullPatientData?.firstName || '', nameField: true },
  { key: 'last_name',   label: 'Last Name',            extract: (r) => r.fullPatientData?.lastName || '', nameField: true },
  { key: 'full_name',   label: 'Full Name (Combined)', extract: (r) => `${r.fullPatientData?.firstName || ''} ${r.fullPatientData?.lastName || ''}`.trim(), nameField: true, combined: true },
  { key: 'email',               label: 'Email',                  extract: (r) => r.fullPatientData?.emailAddress || r.email || '' },
  { key: 'sex',                 label: 'Sex',                    extract: (r) => formatGender(r.fullPatientData?.birthGender || r.gender || '') },
  { key: 'date_of_birth',       label: 'Date of Birth',          extract: (r) => {
    const dob = r.fullPatientData?.dateOfBirth;
    if (!dob) return '';
    const s = String(dob).trim();
    // If already MM/DD/YYYY, keep as-is
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) return s;
    // Handle DD-Mon-YY or DD-Mon-YYYY (e.g. "11-Dec-78")
    const MONTHS = {jan:'01',feb:'02',mar:'03',apr:'04',may:'05',jun:'06',jul:'07',aug:'08',sep:'09',oct:'10',nov:'11',dec:'12'};
    const dmyMatch = s.match(/^(\d{1,2})-([A-Za-z]+)-(\d{2,4})$/);
    if (dmyMatch) {
      const day = dmyMatch[1].padStart(2,'0');
      const mon = MONTHS[dmyMatch[2].toLowerCase().slice(0,3)] || '01';
      let year = parseInt(dmyMatch[3]);
      if (year < 100) year += year > 30 ? 1900 : 2000;
      return `${mon}/${day}/${year}`;
    }
    // Parse ISO dates (YYYY-MM-DD)
    const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      return `${isoMatch[2]}/${isoMatch[3]}/${isoMatch[1]}`;
    }
    const d = new Date(dob);
    if (isNaN(d)) return dob;
    return `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${d.getFullYear()}`;
  }},
  { key: 'phone_number',        label: 'Phone Number',           extract: (r) => formatPhone(r.fullPatientData?.phoneNumber || r.phone || '') },
  { key: 'residential_state',   label: 'Residential State',      extract: (r) => toFullStateName(r.fullPatientData?.stateOfResidence || '') },
  { key: 'residential_zip_code',label: 'Residential Zip Code',   extract: (r) => r.fullPatientData?.eventZip || '' },
  { key: 'is_fasting',          label: 'Is Fasting',             extract: (r) => {
    const readings = r.fullPatientData?.screeningData?.medicalReadings || [];
    const latest = readings.slice().sort((a,b) => new Date(b.visitDate) - new Date(a.visitDate))[0];
    const bloodTest = latest?.bloodTests?.length ? latest.bloodTests[latest.bloodTests.length - 1] : null;
    if (bloodTest?.fastingStatus === true || bloodTest?.fastingStatus === 'true') return 'Yes';
    if (bloodTest?.fastingStatus === false || bloodTest?.fastingStatus === 'false') return 'No';
    return '';
  }},
  { key: 'acknowledged_terms',  label: 'Acknowledged Terms',     extract: (r) => {
    const consent = r.fullPatientData?.consent;
    if (consent?.bhaWaiverConsent === true || consent?.bhaWaiverConsent) return 'I acknowledge';
    return '';
  }},
  { key: 'accepted_responsibility', label: 'Accepted Responsibility', extract: (r) => {
    const consent = r.fullPatientData?.consent;
    if (consent?.endlessHealthAcknowledgment === 'True' || consent?.endlessHealthAcknowledgment === 'true' || consent?.endlessHealthAcknowledgment === true) return 'I accept the Terms and Conditions';
    if (consent?.procedureRisksAcknowledgement === true) return 'I accept the Terms and Conditions';
    if (consent?.acknowledgeContraindications?.length > 0) return 'I accept the Terms and Conditions';
    return '';
  }},
  { key: 'transactional_sms_consent', label: 'Transactional SMS Consent', extract: (r) => {
    const consent = r.fullPatientData?.consent;
    return consent?.transactionalSmsConsent ? 'Yes' : 'No';
  }},
  { key: 'marketing_sms_consent', label: 'Marketing SMS Consent', extract: (r) => {
    const consent = r.fullPatientData?.consent;
    return consent?.marketingSmsConsent ? 'Yes' : 'No';
  }},
];

// Default columns checked — all from the original spec with split names
const DEFAULT_SELECTED = [
  'activation_code', 'collection_date', 'collection_time',
  'first_name', 'last_name', 'email', 'sex', 'date_of_birth',
  'phone_number', 'residential_state', 'residential_zip_code',
  'is_fasting', 'acknowledged_terms', 'accepted_responsibility'
];

const BulkExportModal = ({ visible, onClose, selectedRecords = [], currentFilters = {}, totalRecords = 0 }) => {
  const [selectedColumns, setSelectedColumns] = useState(DEFAULT_SELECTED);
  const [nameMode, setNameMode] = useState('split'); // 'split' | 'combined'
  const [exportScope, setExportScope] = useState('current'); // 'current' | 'all'
  const [exportFormat, setExportFormat] = useState('csv'); // 'csv' | 'xlsx'
  const [fetchingAll, setFetchingAll] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragNodeRef = useRef(null);

  // Columns available based on name mode
  const availableColumns = useMemo(() => {
    return EXPORT_COLUMNS.filter(col => {
      if (nameMode === 'split' && col.combined) return false;
      if (nameMode === 'combined' && col.nameField && !col.combined) return false;
      return true;
    });
  }, [nameMode]);

  // When name mode changes, adjust selected columns
  const handleNameModeChange = (mode) => {
    setNameMode(mode);
    setSelectedColumns(prev => {
      let next = [...prev];
      if (mode === 'combined') {
        next = next.filter(k => k !== 'first_name' && k !== 'last_name');
        if (!next.includes('full_name')) {
          // Insert after activation_code
          const idx = next.indexOf('activation_code');
          next.splice(idx >= 0 ? idx + 1 : 1, 0, 'full_name');
        }
      } else {
        next = next.filter(k => k !== 'full_name');
        if (!next.includes('first_name')) {
          const idx = next.indexOf('activation_code');
          next.splice(idx >= 0 ? idx + 1 : 1, 0, 'first_name');
        }
        if (!next.includes('last_name')) {
          const idx = next.indexOf('first_name');
          next.splice(idx >= 0 ? idx + 1 : 2, 0, 'last_name');
        }
      }
      return next;
    });
  };

  const handleColumnToggle = (columnKey, checked) => {
    setSelectedColumns(prev =>
      checked ? [...prev, columnKey] : prev.filter(k => k !== columnKey)
    );
  };

  const handleDragStart = (e, idx) => {
    setDragIndex(idx);
    dragNodeRef.current = e.target;
    e.dataTransfer.effectAllowed = 'move';
    // Make the dragged row semi-transparent after a tick
    requestAnimationFrame(() => {
      if (dragNodeRef.current) dragNodeRef.current.style.opacity = '0.4';
    });
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (idx !== dragOverIndex) setDragOverIndex(idx);
  };

  const handleDrop = (e, dropIdx) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIdx) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    setSelectedColumns(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(dropIdx, 0, moved);
      return next;
    });
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    if (dragNodeRef.current) dragNodeRef.current.style.opacity = '1';
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleSelectAll = () => {
    setSelectedColumns(availableColumns.map(c => c.key));
  };

  const handleDeselectAll = () => {
    setSelectedColumns([]);
  };

  /** Transform a raw backend visit record into the shape the EXPORT_COLUMNS extractors expect */
  const transformForExport = (record) => ({
    fullPatientData: record.patient || {},
    checkInTimeRaw: record.checkInTime || null,
    completionTimeRaw: record.screeningCompleteTime || record.updatedAt || null,
    email: record.emailAddress || record.patient?.emailAddress || '',
    gender: record.patient?.birthGender || '',
    phone: record.phoneNumber || record.patient?.phoneNumber || '',
  });

  /** Build a CSV string from a list of records */
  const buildCsv = (records) => {
    const columnsToExport = selectedColumns
      .map(key => availableColumns.find(c => c.key === key))
      .filter(Boolean);

    const headers = columnsToExport.map(c => c.key);
    const rows = records.map(record => {
      return columnsToExport.map(col => {
        let value = col.extract(record);
        value = String(value ?? '');
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          value = `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      });
    });

    return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  };

  /** Build an XLSX Blob from a list of records */
  const buildXlsx = (records) => {
    const columnsToExport = selectedColumns
      .map(key => availableColumns.find(c => c.key === key))
      .filter(Boolean);

    const headers = columnsToExport.map(c => c.label);
    const rows = records.map(record =>
      columnsToExport.map(col => {
        let value = col.extract(record);
        return value ?? '';
      })
    );

    const wsData = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Auto-size columns
    ws['!cols'] = headers.map((h, i) => {
      let maxLen = h.length;
      rows.forEach(row => {
        const cellLen = String(row[i] ?? '').length;
        if (cellLen > maxLen) maxLen = cellLen;
      });
      return { wch: Math.min(maxLen + 2, 50) };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Members Export');
    const xlsxBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    return new Blob([xlsxBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  };

  /** Trigger browser download of a CSV string */
  const downloadCsv = (csvString, count) => {
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `members-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    message.success(`Exported ${count} record(s) successfully`);
    onClose();
  };

  /** Trigger browser download of an XLSX blob */
  const downloadXlsx = (blob, count) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `members-export-${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    message.success(`Exported ${count} record(s) successfully`);
    onClose();
  };

  /** Download records in the selected format */
  const downloadRecords = (records, count) => {
    if (exportFormat === 'xlsx') {
      const blob = buildXlsx(records);
      downloadXlsx(blob, count);
    } else {
      const csvString = buildCsv(records);
      downloadCsv(csvString, count);
    }
  };

  const handleExport = async () => {
    if (selectedColumns.length === 0) {
      message.warning('Please select at least one column to export');
      return;
    }

    // --- Export all matching records ---
    if (exportScope === 'all') {
      setFetchingAll(true);
      try {
        const response = await PatientService.getAllForExport(currentFilters);
        const allData = (response.data || []).map(transformForExport);
        if (allData.length === 0) {
          message.warning('No records found matching current filters');
          return;
        }
        downloadRecords(allData, allData.length);
      } catch (err) {
        console.error('Export all failed:', err);
        message.error('Failed to fetch all records for export');
      } finally {
        setFetchingAll(false);
      }
      return;
    }

    // --- Export current page / selection ---
    if (selectedRecords.length === 0) {
      message.warning('No records selected for export');
      return;
    }

    downloadRecords(selectedRecords, selectedRecords.length);
  };

  return (
    <Modal
      title={
        <Space>
          <DownloadOutlined />
          <span>Bulk Export — {exportScope === 'all' ? `${totalRecords} total` : `${selectedRecords.length} record(s) selected`}</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={fetchingAll}>Cancel</Button>,
        <Button
          key="export"
          type="primary"
          icon={fetchingAll ? undefined : <DownloadOutlined />}
          onClick={handleExport}
          loading={fetchingAll}
          disabled={selectedColumns.length === 0 || (exportScope === 'current' && selectedRecords.length === 0)}
          style={{ backgroundColor: '#000', borderColor: '#000' }}
        >
          {fetchingAll ? 'Fetching records…' : 'Export'}
        </Button>
      ]}
    >
      {/* Export Scope */}
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Export Scope</Text>
        <Radio.Group
          value={exportScope}
          onChange={(e) => setExportScope(e.target.value)}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value="current">
            Current Page ({selectedRecords.length})
          </Radio.Button>
          <Radio.Button value="all">
            <CloudDownloadOutlined style={{ marginRight: 4 }} />
            All Records ({totalRecords})
          </Radio.Button>
        </Radio.Group>
        {exportScope === 'all' && (
          <div style={{ marginTop: 6, fontSize: 12, color: COLORS.textMuted }}>
            All {totalRecords} records matching your current filters will be exported.
          </div>
        )}
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Name Mode */}
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Name Format</Text>
        <Radio.Group
          value={nameMode}
          onChange={(e) => handleNameModeChange(e.target.value)}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value="split">Split (First / Last)</Radio.Button>
          <Radio.Button value="combined">Combined (Full Name)</Radio.Button>
        </Radio.Group>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Export Format */}
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Export Format</Text>
        <Radio.Group
          value={exportFormat}
          onChange={(e) => setExportFormat(e.target.value)}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value="csv">CSV (.csv)</Radio.Button>
          <Radio.Button value="xlsx">Excel (.xlsx)</Radio.Button>
        </Radio.Group>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Column Selection */}
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text strong>Select &amp; Reorder Columns</Text>
        <Space>
          <Button size="small" icon={<CheckSquareOutlined />} onClick={handleSelectAll}>All</Button>
          <Button size="small" icon={<BorderOutlined />} onClick={handleDeselectAll}>None</Button>
        </Space>
      </div>

      <div style={{
        maxHeight: 380,
        overflowY: 'auto',
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        padding: '8px 12px'
      }}>
        {/* Selected columns first, in user-defined order */}
        {selectedColumns
          .map(key => availableColumns.find(c => c.key === key))
          .filter(Boolean)
          .map((col, idx) => (
            <div
              key={col.key}
              draggable
              onDragStart={(e) => handleDragStart(e, idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDrop={(e) => handleDrop(e, idx)}
              onDragEnd={handleDragEnd}
              style={{
                padding: '6px 8px',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: dragOverIndex === idx && dragIndex !== null ? '#e6f7ff' : '#fafffe',
                borderTop: dragOverIndex === idx && dragIndex !== null && dragIndex > idx ? '2px solid #1890ff' : 'none',
                borderBottomColor: dragOverIndex === idx && dragIndex !== null && dragIndex < idx ? '#1890ff' : '#f0f0f0',
                borderBottomWidth: dragOverIndex === idx && dragIndex !== null && dragIndex < idx ? 2 : 1,
                cursor: 'grab',
                transition: 'background 0.15s, border-color 0.15s',
                userSelect: 'none'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <HolderOutlined style={{ color: '#bbb', fontSize: 14, cursor: 'grab' }} />
                <Checkbox
                  checked
                  onChange={() => handleColumnToggle(col.key, false)}
                >
                  <span style={{ fontSize: 13 }}>{col.label}</span>
                </Checkbox>
              </div>
              <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{col.key}</Text>
            </div>
          ))}
        {/* Unselected columns below */}
        {availableColumns
          .filter(col => !selectedColumns.includes(col.key))
          .map(col => (
            <div
              key={col.key}
              style={{
                padding: '4px 0',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                opacity: 0.6
              }}
            >
              <Checkbox
                checked={false}
                onChange={() => handleColumnToggle(col.key, true)}
              >
                <span style={{ fontSize: 13 }}>{col.label}</span>
              </Checkbox>
              <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{col.key}</Text>
            </div>
          ))}
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Preview of output format */}
      <div style={{
        background: '#f8f9fa',
        padding: 12,
        borderRadius: 8,
        fontSize: 12,
        color: COLORS.textMuted
      }}>
        <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Output Format:</Text>
        <ul style={{ margin: 0, paddingLeft: 16 }}>
          <li>{exportFormat === 'xlsx' ? 'Excel spreadsheet (.xlsx)' : 'Comma-separated values (.csv)'}</li>
          <li><code>activation_code</code> → Patient&apos;s Testing Code</li>
          <li><code>date_of_birth</code> → Formatted as &quot;Mon DD, YYYY&quot;</li>
          <li><code>acknowledged_terms</code> → &quot;I acknowledge&quot;</li>
          <li><code>accepted_responsibility</code> → &quot;I accept the Terms and Conditions&quot;</li>
        </ul>
      </div>
    </Modal>
  );
};

export default BulkExportModal;
